// Twin Gaze (owner approved the concept 2026-11-02: 'Make them all'): two thin, hot lasers fire from Aren's eyes and sweep
// across his front like closing scissors, crossing in an X and carving glowing lines into the ground. Render-only: reads
// sim.gaze / sim.gazeBurns and the 'gaze' / 'gazePass' / 'gazeEnd' events; decides nothing about gameplay.
//  - eye glints: two small 4-point flares pinned to the real eyes (found on the authored model's head), swelling during the
//    wind-up and flickering hot while the beams run
//  - beams: razor-thin camera-facing ribbons from each eye down to where the beam meets the ground: a white-hot core in a
//    molten-orange sheath with a crisp edge (normal blend, so it stays saturated over bright grass), plus a faint additive
//    heat haze around it
//  - landing points: a hot flare, sparks and molten spatter where each beam touches the ground; the ground is charred along
//    the path (ground.ts burn map) and a cooling molten line glows behind it (ground glow strips), so the two cut lines
//    read as an X burnt into the field. Pyre Sight's burning lines (sim.gazeBurns) keep glowing and throw flames.
// Distinct from Nova Lance (a wide charged blue beam from the hands) and Finger Beam (a crimson needle volley): thin,
// sweeping, orange-white, from the eyes. One dynamic mesh per layer, one instanced flare mesh; no per-frame allocations.
import * as THREE from 'three/webgpu';
import { abs, attribute, float, length, max, mix, smoothstep, uv, vec2, vec3 } from 'three/tsl';
import type { Sim, SimEvent } from '../core/sim';
import type { Fx } from './fx';
import { FLASH } from './fx';
import type { GroundGlow } from './groundGlow';
import type { GltfHero } from './heroGltf';
import type { HeroRig } from './models';

const SEG = 20;                 // segments along a beam
const TRAIL_CAP = 128;          // landing-trail points kept per beam
const TRAIL_STEP = 0.03;        // s between trail points
const TRAIL_LIFE = 0.9;         // s a trail point lights the ground glow
const SCORCH_LIFE = 1.9, SCORCH_LIFE_EVO = 3.2; // s the molten scorch ribbon lasts (cools, then fades)
/** identity colours: white-hot core, molten orange sheath, deep red cooling line */
const SHEATH = new THREE.Color(0xff6a12), CORE = new THREE.Color(1.6, 1.45, 1.1), HAZE = 0xff8a30;
const HOT_LINE = new THREE.Color(0xffb040), COOL_LINE = new THREE.Color(0xff2a08);

type GazeEv = Extract<SimEvent, { t: 'gaze' }>;

const _a = new THREE.Vector3(), _v = new THREE.Vector3(), _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion();
const _m = new THREE.Matrix4(), _s = new THREE.Vector3(), _c = new THREE.Color(), _up = new THREE.Vector3(0, 1, 0), _pq = new THREE.Quaternion();

interface Trail { x: Float32Array; z: Float32Array; t: Float32Array; head: number; n: number; acc: number }

export class TwinGazeFx {
  /** 0..1 how strongly the hero is gazing (view.ts squares the standing hero up to aimYaw while > 0.3) */
  pose = 0; aimYaw = 0;
  /** QA counters */
  casts = 0; passes = 0; eyesFound = false;
  /** world positions of both eyes this frame (QA) */
  readonly eyes = [new THREE.Vector3(), new THREE.Vector3()];
  private glint = 0; private on = 0; private evo = false;
  private beam: THREE.Mesh; private haze: THREE.Mesh;
  private flares: THREE.InstancedMesh;
  private geoB = new THREE.BufferGeometry(); private geoH = new THREE.BufferGeometry();
  private posB: Float32Array; private posH: Float32Array; private attB: Float32Array; private attH: Float32Array;
  private trails: Trail[] = [0, 1].map(() => ({ x: new Float32Array(TRAIL_CAP), z: new Float32Array(TRAIL_CAP), t: new Float32Array(TRAIL_CAP), head: 0, n: 0, acc: 0 }));
  private land = [new THREE.Vector3(), new THREE.Vector3()];
  private eyeLocal: THREE.Vector3[] | null = null; private headBone: THREE.Bone | null = null; private neckBone: THREE.Bone | null = null; private eyeHero: GltfHero | null = null;
  private twistBase = [new THREE.Quaternion(), new THREE.Quaternion()]; private twistOut = [new THREE.Quaternion(), new THREE.Quaternion()]; private twisted = false;
  private sparkAcc = 0; private flameAcc = 0;
  private scorch: THREE.Mesh; private geoS = new THREE.BufferGeometry(); private posS!: Float32Array; private attS!: Float32Array;

  constructor(scene: THREE.Scene, private fx: Fx, private groundAt: (x: number, z: number) => number, private burn: (x: number, z: number, r: number) => void) {
    // ribbons: 2 beams × (SEG+1) × 2 vertices; per vertex 'gz' = (alpha, core half-width share, heat, unused)
    const make = (geo: THREE.BufferGeometry) => {
      const nV = 2 * (SEG + 1) * 2, pos = new Float32Array(nV * 3), att = new Float32Array(nV * 4), uvs = new Float32Array(nV * 2), idx = new Uint16Array(2 * SEG * 6);
      for (let b = 0; b < 2; b++) {
        for (let i = 0; i <= SEG; i++) { const v = (b * (SEG + 1) + i) * 2; uvs.set([i / SEG, 0, i / SEG, 1], v * 2); }
        for (let i = 0; i < SEG; i++) { const v = (b * (SEG + 1) + i) * 2, o = (b * SEG + i) * 6; idx.set([v, v + 1, v + 2, v + 2, v + 1, v + 3], o); }
      }
      geo.setAttribute('position', new THREE.BufferAttribute(pos, 3).setUsage(THREE.DynamicDrawUsage));
      geo.setAttribute('gz', new THREE.BufferAttribute(att, 4).setUsage(THREE.DynamicDrawUsage));
      geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
      geo.setIndex(new THREE.BufferAttribute(idx, 1)); geo.setDrawRange(0, 0);
      return [pos, att] as const;
    };
    [this.posB, this.attB] = make(this.geoB); [this.posH, this.attH] = make(this.geoH);
    const across = abs(uv().y.sub(0.5)).mul(2);
    // beam: crisp molten sheath, hard white core (cel), slightly brighter toward the ground end where it burns
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const a = attribute('gz', 'vec4') as any;
    const mb = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
    const core = smoothstep(a.y, a.y.mul(0.55), across);
    const rim = smoothstep(0.62, 0.95, across); // darker red-orange outline so it reads on bright grass
    const sheath = mix(vec3(SHEATH.r * 1.25, SHEATH.g * 1.15, SHEATH.b), vec3(0.85, 0.12, 0.02), rim);
    mb.colorNode = mix(sheath, vec3(CORE.r, CORE.g, CORE.b), core).mul(a.z.mul(0.35).add(0.85));
    mb.opacityNode = smoothstep(1.0, 0.86, across).mul(a.x);
    this.beam = new THREE.Mesh(this.geoB, mb); this.beam.frustumCulled = false; this.beam.renderOrder = 6;
    // haze: soft additive glow around the beam (kept faint: the cel core carries the look)
    const mh = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false, blending: THREE.AdditiveBlending });
    _c.setHex(HAZE);
    mh.colorNode = vec3(_c.r, _c.g, _c.b).mul(smoothstep(1.0, 0.0, across).pow(2).mul(a.x).mul(0.55));
    this.haze = new THREE.Mesh(this.geoH, mh); this.haze.frustumCulled = false; this.haze.renderOrder = 5;
    // flares: 0,1 = eye glints, 2,3 = landing points. A procedural 4-point anime glint: long horizontal streak, shorter
    // vertical one, white-hot centre; instance colour carries tint × intensity
    const mf = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, depthTest: false, fog: false, blending: THREE.AdditiveBlending });
    const p = uv().sub(0.5), ax = abs(p.x), ay = abs(p.y);
    const streakH = smoothstep(0.035, 0.0, ay.sub(ax.mul(-0.06).add(0.03)).max(0)).mul(smoothstep(0.5, 0.05, ax));
    const streakV = smoothstep(0.03, 0.0, ax.sub(ay.mul(-0.05).add(0.025)).max(0)).mul(smoothstep(0.32, 0.03, ay)).mul(0.8);
    const dot = smoothstep(0.16, 0.0, length(vec2(p.x, p.y)));
    const glow = smoothstep(0.5, 0.0, length(vec2(p.x, p.y))).pow(3).mul(0.5);
    // the instance colour (tint × intensity) multiplies this; the centre lifts green/blue so it burns white
    const hot = dot.max(streakH.mul(0.35));
    mf.colorNode = vec3(float(1), hot.mul(0.9).add(1), hot.mul(2.6).add(1)).mul(max(max(streakH, streakV), max(dot, glow)));
    this.flares = new THREE.InstancedMesh(new THREE.PlaneGeometry(1, 1), mf, 4);
    this.flares.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(12), 3).setUsage(THREE.DynamicDrawUsage) as THREE.InstancedBufferAttribute;
    this.flares.count = 0; this.flares.frustumCulled = false; this.flares.renderOrder = 9;
    // scorch: a ribbon lying on the ground along each beam's landing trail: a white-hot molten core in a dark charred groove
    // that cools through orange and deep red to char, then fades (the ground's own burn map keeps a faint scar after it).
    // Normal blending so it reads on bright grass; per vertex 'gs' = (heat 0..1, alpha, unused, unused)
    {
      const nV = 2 * TRAIL_CAP * 2, uvs = new Float32Array(nV * 2), idx = new Uint16Array(2 * (TRAIL_CAP - 1) * 6);
      for (let b = 0; b < 2; b++) {
        for (let i = 0; i < TRAIL_CAP; i++) { const v = (b * TRAIL_CAP + i) * 2; uvs.set([i / (TRAIL_CAP - 1), 0, i / (TRAIL_CAP - 1), 1], v * 2); }
        for (let i = 0; i < TRAIL_CAP - 1; i++) { const v = (b * TRAIL_CAP + i) * 2, o = (b * (TRAIL_CAP - 1) + i) * 6; idx.set([v, v + 1, v + 2, v + 2, v + 1, v + 3], o); }
      }
      this.posS = new Float32Array(nV * 3); this.attS = new Float32Array(nV * 4);
      this.geoS.setAttribute('position', new THREE.BufferAttribute(this.posS, 3).setUsage(THREE.DynamicDrawUsage));
      this.geoS.setAttribute('gs', new THREE.BufferAttribute(this.attS, 4).setUsage(THREE.DynamicDrawUsage));
      this.geoS.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
      this.geoS.setIndex(new THREE.BufferAttribute(idx, 1)); this.geoS.setDrawRange(0, 0);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const s = attribute('gs', 'vec4') as any;
      const ms = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -4 });
      const heat = s.x, glowW = heat.mul(0.32).add(0.12);                     // the molten core narrows as it cools
      const molten = smoothstep(glowW, glowW.mul(0.35), across);
      const hotCol = mix(mix(vec3(0.45, 0.04, 0.0), vec3(1.4, 0.42, 0.06), smoothstep(0.15, 0.55, heat)), vec3(1.7, 1.05, 0.35), smoothstep(0.72, 0.97, heat));
      ms.colorNode = mix(vec3(0.07, 0.035, 0.025), hotCol, molten.mul(smoothstep(0.02, 0.2, heat)));
      ms.opacityNode = smoothstep(1.0, 0.45, across).mul(0.78).max(molten.mul(smoothstep(0.02, 0.2, heat))).mul(s.y);
      this.scorch = new THREE.Mesh(this.geoS, ms); this.scorch.frustumCulled = false; this.scorch.renderOrder = 2;
    }
    scene.add(this.beam, this.haze, this.flares, this.scorch);
  }

  /** 'gaze' event: the eye glint starts (beams follow in GAZE_WIND s) */
  cast(ev: GazeEv): void { this.casts++; this.evo = ev.evolved; this.aimYaw = Math.atan2(ev.dx, ev.dz); }
  pass(): void { this.passes++; }

  /** the authored hero's eyes: the dark eye vertices of the skinned body near the head, mapped into the head bone's space
   *  through its inverse bind matrix (so they follow every head turn exactly). False = not found (fallback: rig head). */
  private findEyes(h: GltfHero): boolean {
    this.eyeHero = h; this.eyeLocal = null; this.headBone = null; this.neckBone = null;
    const meshes: THREE.SkinnedMesh[] = [];
    h.root.traverse((o) => {
      const sm = o as THREE.SkinnedMesh;
      if ((o as THREE.Bone).isBone && o.name === 'neck') this.neckBone = o as THREE.Bone;
      if (sm.isSkinnedMesh && !h.outlineMeshes.includes(sm) && sm.geometry.attributes.color && sm.skeleton.bones.some((b) => b.name === 'head')) meshes.push(sm);
    });
    const sum = [new THREE.Vector3(), new THREE.Vector3()], cnt = [0, 0];
    for (const sm of meshes) {
      const bi = sm.skeleton.bones.findIndex((b) => b.name === 'head');
      this.headBone = sm.skeleton.bones[bi];
      const pos = sm.geometry.attributes.position, col = sm.geometry.attributes.color, inv = sm.skeleton.boneInverses[bi];
      for (let i = 0; i < pos.count; i++) {
        const r = col.getX(i), b = col.getZ(i);
        if (!(b > r * 3 && b < 0.32 && r < 0.12)) continue; // the eyes' dark navy (hair and armour are greyer)
        _v.fromBufferAttribute(pos, i).applyMatrix4(inv);     // into the head bone's own space
        if (_v.length() > 0.25 || _v.y < 0.03) continue;     // on the head, above the neck joint (the dark-blue suit collar is below it)
        const s = _v.x >= 0 ? 0 : 1; sum[s].add(_v); cnt[s]++;
      }
    }
    if (!this.headBone || cnt[0] < 3 || cnt[1] < 3) return false;
    this.eyeLocal = [sum[0].divideScalar(cnt[0]), sum[1].divideScalar(cnt[1])];
    return true;
  }

  /** Per frame (after the hero is posed). hx/hz = drawn hero position; alpha = sim interpolation. */
  update(dt: number, sim: Sim, cam: THREE.Camera, rig: HeroRig, gltf: GltfHero | null, hx: number, hz: number, alpha: number, moving: boolean): void {
    const g = sim.gaze;
    const active = g.phase > 0;
    this.glint += ((g.phase === 1 ? 0.85 + g.t * 1.2 : g.phase === 2 ? 1 : 0) - this.glint) * Math.min(1, dt * (active ? 20 : 8));
    this.on += ((g.phase === 2 ? 1 : 0) - this.on) * Math.min(1, dt * (g.phase === 2 ? 40 : 14));
    this.pose += ((active ? 1 : 0) - this.pose) * Math.min(1, dt * (active ? 16 : 6));
    if (active) this.aimYaw = Math.atan2(g.ax, g.az);
    // head turn toward the sweep while he runs (the standing body squares up in view.ts): neck + head, post-mixer
    this.headTwist(gltf, moving && this.pose > 0.05 ? this.pose : 0);
    if (!active && this.glint < 0.01 && this.on < 0.01) { // idle: only the cooling scorch lines (and Pyre Sight's fire) remain
      this.writeRibbons(0, cam.position); this.flares.count = 0;
      for (const tr of this.trails) for (let k = 0, i = tr.head; k < tr.n; k++, i = (i - 1 + TRAIL_CAP) % TRAIL_CAP) tr.t[i] += dt;
      if (this.trails[0].n || this.trails[1].n || this.geoS.drawRange.count) this.writeScorch();
      this.flames(dt, sim);
      return;
    }
    // eyes
    if (gltf && gltf.ready && gltf.root.visible) {
      if (this.eyeHero !== gltf) this.eyesFound = this.findEyes(gltf);
      if (this.eyeLocal && this.headBone) { gltf.root.updateMatrixWorld(true); for (let s = 0; s < 2; s++) this.headBone.localToWorld(this.eyes[s].copy(this.eyeLocal[s])); }
      else this.rigEyes(rig);
    } else this.rigEyes(rig);
    // eyes sorted to the beams: beam 0 starts on the aim's (-az, ax) side; it leaves from the eye on that same side, so the
    // two beams start splayed and cross as they sweep past each other (scissor X)
    const sx = -g.az, sz = g.ax, side = (v: THREE.Vector3) => (v.x - hx) * sx + (v.z - hz) * sz;
    if (side(this.eyes[0]) < side(this.eyes[1])) { _v.copy(this.eyes[0]); this.eyes[0].copy(this.eyes[1]); this.eyes[1].copy(_v); }

    // beams: landing points from the (interpolated) sim angles, drawn from the eyes
    const show = this.on > 0.02;
    for (let b = 0; b < 2; b++) {
      const th = g.thP[b] + (g.th[b] - g.thP[b]) * alpha, d = g.dP[b] + (g.d[b] - g.dP[b]) * alpha, c = Math.cos(th), s = Math.sin(th);
      const lx = hx + (g.ax * c + sx * s) * d, lz = hz + (g.az * c + sz * s) * d;
      this.land[b].set(lx, this.groundAt(lx, lz) + 0.06, lz);
    }
    this.writeRibbons(show ? this.on : 0, cam.position);
    // landing points: char + cooling molten line, sparks and spatter
    if (g.phase === 2) {
      for (let b = 0; b < 2; b++) {
        const tr = this.trails[b], L = this.land[b];
        tr.acc += dt;
        if (tr.acc >= TRAIL_STEP || tr.n === 0) {
          tr.acc = 0; tr.head = (tr.head + 1) % TRAIL_CAP; tr.x[tr.head] = L.x; tr.z[tr.head] = L.z; tr.t[tr.head] = 0; tr.n = Math.min(TRAIL_CAP, tr.n + 1);
          this.burn(L.x, L.z, this.evo ? 0.62 : 0.5);
        }
      }
      this.sparkAcc += dt * 70;
      for (; this.sparkAcc >= 1; this.sparkAcc--) {
        const L = this.land[Math.random() < 0.5 ? 0 : 1], a = Math.random() * 6.283, sp = 2 + Math.random() * 5;
        this.fx.particles.emitDir(L.x, L.y + 0.08, L.z, Math.cos(a) * sp, 2.5 + Math.random() * 4.5, Math.sin(a) * sp, Math.random() < 0.4 ? 0xfff0c0 : 0xff7a20, 0.12 + Math.random() * 0.08, 0.22 + Math.random() * 0.2, 2.6);
        if (Math.random() < 0.25) this.fx.particles.emitDir(L.x, L.y + 0.3, L.z, (Math.random() - 0.5), 1.2 + Math.random(), (Math.random() - 0.5), 0x5a3a30, 0.5, 0.6, 0.5); // a wisp of smoke
      }
      if (FLASH.dim >= 1 && Math.random() < dt * 12) this.fx.lights.flash(this.land[0].x, this.land[0].y + 0.5, this.land[0].z, 0xff7a20, 4, 0.08, 6);
    }
    for (const tr of this.trails) for (let k = 0, i = tr.head; k < tr.n; k++, i = (i - 1 + TRAIL_CAP) % TRAIL_CAP) tr.t[i] += dt;
    this.writeScorch();
    this.flames(dt, sim);
    // flares: eye glints (flicker while firing) and the landing points
    const camQ = cam.quaternion; let n = 0;
    const ic = this.flares.instanceColor!.array as Float32Array;
    const put = (p: THREE.Vector3, size: number, r: number, gg: number, bb: number, k: number, roll: number) => {
      if (k <= 0.01) return;
      _q2.setFromAxisAngle(_v.set(0, 0, 1), roll); _q.copy(camQ).multiply(_q2);
      this.flares.setMatrixAt(n, _m.compose(p, _q, _s.setScalar(size)));
      ic[n * 3] = r * k; ic[n * 3 + 1] = gg * k; ic[n * 3 + 2] = bb * k; n++;
    };
    const flick = g.phase === 2 ? 0.85 + FLASH.flick(0.3) * Math.random() : 1, gl = this.glint * flick * FLASH.dim;
    // the glint is drawn over the head (no depth test); dim it while the face points away from the camera
    const yaw = gltf && gltf.ready && gltf.root.visible ? gltf.root.rotation.y : rig.root.rotation.y;
    const tcx = cam.position.x - hx, tcz = cam.position.z - hz, tcl = Math.hypot(tcx, tcz) || 1;
    const faceK = 0.35 + 0.65 * Math.max(0, Math.min(1, ((Math.sin(yaw) * tcx + Math.cos(yaw) * tcz) / tcl) * 1.5 + 0.5));
    for (let s = 0; s < 2; s++) put(this.eyes[s], (0.3 + 0.16 * this.glint) * (this.evo ? 1.2 : 1), 2.4, 1.3, 0.5, gl * faceK, s ? 0.08 : -0.08);
    if (show) for (let b = 0; b < 2; b++) put(this.land[b], (this.evo ? 2.3 : 1.9) + Math.random() * 0.5, 2.6, 1.0, 0.25, this.on * FLASH.dim, Math.random() * 0.4 - 0.2);
    this.flares.count = n; this.flares.instanceMatrix.needsUpdate = true; this.flares.instanceColor!.needsUpdate = true;
  }

  /** Pyre Sight: flames licking up along the sim's burning lines */
  private flames(dt: number, sim: Sim): void {
    if (!sim.evolved.has('gaze')) return;
    this.flameAcc = Math.min(this.flameAcc + dt * 110, 4);
    const burns = sim.gazeBurns;
    for (let tries = 0; this.flameAcc >= 1 && tries < 64; tries++) {
      const bb = burns[(Math.random() * burns.length) | 0]; if (!bb.alive) continue;
      this.flameAcc--;
      const u = Math.random(), x = bb.x0 + (bb.x1 - bb.x0) * u, z = bb.z0 + (bb.z1 - bb.z0) * u, k = bb.t / bb.life;
      this.fx.particles.emitDir(x, this.groundAt(x, z) + 0.15, z, (Math.random() - 0.5) * 0.6, 1.6 + Math.random() * 2 * k, (Math.random() - 0.5) * 0.6, Math.random() < 0.5 ? 0xffa030 : 0xff5a14, 0.5 + 0.45 * k, 0.45, 2.4);
    }
  }

  private rigEyes(rig: HeroRig): void {
    rig.root.updateMatrixWorld(true);
    rig.head.localToWorld(this.eyes[0].set(0.095, 0.0, 0.24)); rig.head.localToWorld(this.eyes[1].set(-0.095, 0.0, 0.24));
  }

  /** turn the neck and head toward the sweep (k = weight). The mixer may skip writing an unchanged bone, so the previous
   *  frame's twist is undone first when the bone still holds exactly what was written. */
  private headTwist(gltf: GltfHero | null, k: number): void {
    const bones = [this.neckBone, this.headBone];
    if (!gltf || !gltf.ready || !this.headBone) { this.twisted = false; return; }
    if (this.twisted) for (let i = 0; i < 2; i++) { const b = bones[i]; if (b && b.quaternion.equals(this.twistOut[i])) b.quaternion.copy(this.twistBase[i]); }
    this.twisted = false;
    if (k <= 0.01) return;
    let rel = this.aimYaw - gltf.root.rotation.y; rel = Math.atan2(Math.sin(rel), Math.cos(rel));
    rel = Math.max(-1.1, Math.min(1.1, rel)) * k;
    if (Math.abs(rel) < 1e-3) return;
    gltf.root.updateMatrixWorld(true);
    for (let i = 0; i < 2; i++) {
      const b = bones[i]; if (!b || !b.parent) continue;
      this.twistBase[i].copy(b.quaternion);
      b.parent.getWorldQuaternion(_pq);
      _a.copy(_up).applyQuaternion(_pq.invert()).normalize();
      b.quaternion.premultiply(_q.setFromAxisAngle(_a, rel * (i ? 0.55 : 0.45)));
      this.twistOut[i].copy(b.quaternion);
      b.updateMatrixWorld(true);
    }
    this.twisted = true;
  }

  /** both ribbons (beam + haze) from the eyes to the landing points; w = 0 hides them */
  private writeRibbons(k: number, camPos: THREE.Vector3): void {
    if (k <= 0.01) { this.geoB.setDrawRange(0, 0); this.geoH.setDrawRange(0, 0); return; }
    for (let b = 0; b < 2; b++) {
      const E = this.eyes[b], L = this.land[b];
      const dx = L.x - E.x, dy = L.y - E.y, dz = L.z - E.z, len = Math.hypot(dx, dy, dz) || 1;
      for (let i = 0; i <= SEG; i++) {
        const u = i / SEG, px = E.x + dx * u, py = E.y + dy * u, pz = E.z + dz * u;
        const taper = Math.min(1, (u * len) / 0.5) * 0.65 + 0.35;              // pinched at the eye
        const wob = 1 + 0.12 * Math.sin(u * 40 - performance.now() * 0.06 + b * 2);
        const w = (this.evo ? 0.32 : 0.26) * taper * wob * (0.4 + 0.6 * k), wh = w * 3.6;
        const vx = camPos.x - px, vy = camPos.y - py, vz = camPos.z - pz;
        let sx = dy * vz - dz * vy, sy = dz * vx - dx * vz, sz = dx * vy - dy * vx; const sl = Math.hypot(sx, sy, sz) || 1; sx /= sl; sy /= sl; sz /= sl;
        const o = ((b * (SEG + 1) + i) * 2) * 3;
        for (const [P, ww] of [[this.posB, w], [this.posH, wh]] as [Float32Array, number][]) {
          P[o] = px - sx * ww * 0.5; P[o + 1] = py - sy * ww * 0.5; P[o + 2] = pz - sz * ww * 0.5;
          P[o + 3] = px + sx * ww * 0.5; P[o + 4] = py + sy * ww * 0.5; P[o + 5] = pz + sz * ww * 0.5;
        }
        const q = ((b * (SEG + 1) + i) * 2) * 4, heat = u;
        for (let v = 0; v < 2; v++) {
          this.attB[q + v * 4] = Math.min(1, k * 1.4); this.attB[q + v * 4 + 1] = 0.42; this.attB[q + v * 4 + 2] = heat;
          this.attH[q + v * 4] = k * (0.5 + 0.5 * u);
        }
      }
    }
    for (const geo of [this.geoB, this.geoH]) {
      geo.setDrawRange(0, 2 * SEG * 6);
      (geo.attributes.position as THREE.BufferAttribute).needsUpdate = true; (geo.attributes.gz as THREE.BufferAttribute).needsUpdate = true;
    }
  }

  /** the scorch ribbons along both landing trails (newest point first; unused slots collapse onto the last point) */
  private writeScorch(): void {
    const life = this.evo ? SCORCH_LIFE_EVO : SCORCH_LIFE, P = this.posS, S = this.attS, hwBase = this.evo ? 0.32 : 0.27;
    let any = false;
    for (let b = 0; b < 2; b++) {
      const tr = this.trails[b];
      while (tr.n > 0 && tr.t[(tr.head - tr.n + 1 + TRAIL_CAP) % TRAIL_CAP] > life) tr.n--; // forget points that have cooled away
      let lx = 0, ly = 0, lz = 0;
      for (let k = 0; k < TRAIL_CAP; k++) {
        const v = (b * TRAIL_CAP + k) * 2, o = v * 3, q = v * 4;
        if (k >= tr.n) { for (let s = 0; s < 2; s++) { P[o + s * 3] = lx; P[o + s * 3 + 1] = ly; P[o + s * 3 + 2] = lz; S[q + s * 4 + 1] = 0; } continue; }
        const i = (tr.head - k + TRAIL_CAP) % TRAIL_CAP, x = tr.x[i], z = tr.z[i], age = tr.t[i];
        const ia = (tr.head - Math.max(0, k - 1) + TRAIL_CAP) % TRAIL_CAP, ib = (tr.head - Math.min(tr.n - 1, k + 1) + TRAIL_CAP) % TRAIL_CAP;
        let tx = tr.x[ia] - tr.x[ib], tz = tr.z[ia] - tr.z[ib]; const tl = Math.hypot(tx, tz);
        if (tl < 1e-4) { tx = 1; tz = 0; } else { tx /= tl; tz /= tl; }
        const heat = this.evo ? Math.max(0, 1 - age / 1.1, 0.42 * Math.min(1, (SCORCH_LIFE_EVO - 0.6 - age) / 0.5)) : Math.max(0, 1 - age / 1.1), al = Math.min(1, (life - age) / (life * 0.4)), hw = hwBase * (0.75 + 0.35 * heat);
        const y = this.groundAt(x, z) + 0.035; lx = x; ly = y; lz = z;
        P[o] = x - tz * hw; P[o + 1] = y; P[o + 2] = z + tx * hw; P[o + 3] = x + tz * hw; P[o + 4] = y; P[o + 5] = z - tx * hw;
        for (let s = 0; s < 2; s++) { S[q + s * 4] = heat; S[q + s * 4 + 1] = Math.max(0, al); }
        any = true;
      }
    }
    this.geoS.setDrawRange(0, any ? 2 * (TRAIL_CAP - 1) * 6 : 0);
    if (any) { (this.geoS.attributes.position as THREE.BufferAttribute).needsUpdate = true; (this.geoS.attributes.gs as THREE.BufferAttribute).needsUpdate = true; }
  }

  /** Ground glow: the hot landing points and the cooling molten lines behind them (groundGlow.ts) */
  glowPools(g: GroundGlow, sim: Sim): void {
    if (this.on > 0.05) for (const L of this.land) g.blob(L.x, L.y, L.z, this.evo ? 1.5 : 1.2, 0xff8a2a, 1.4 * this.on, 1);
    const life = this.evo ? Math.max(TRAIL_LIFE, sim.gaze.burn || 2.5) : TRAIL_LIFE;
    let budget = 70;
    for (const tr of this.trails) {
      for (let k = 0, i = tr.head; k + 2 < tr.n && budget > 0; k += 2) {
        const j = (i - 2 + TRAIL_CAP) % TRAIL_CAP, age = tr.t[i];
        if (age > life) break;
        const f = 1 - age / life, heat = Math.min(1, f * 1.6);
        _c.copy(COOL_LINE).lerp(HOT_LINE, heat);
        g.strip(tr.x[j], tr.z[j], tr.x[i], tr.z[i], this.groundAt(tr.x[i], tr.z[i]) + 0.1, this.evo ? 0.42 : 0.32, _c.getHex(), 1.3 * f * f + 0.15 * f, 1, 1);
        budget--; i = j;
      }
    }
    if (sim.evolved.has('gaze')) { // Pyre Sight: the sim's burning lines (older than the trail kept above) keep a low red glow
      let n = 0;
      for (const b of sim.gazeBurns) {
        if (!b.alive || n++ > 40 || b.t > b.life - TRAIL_LIFE) continue;
        const k = b.t / b.life;
        g.strip(b.x0, b.z0, b.x1, b.z1, this.groundAt(b.x0, b.z0) + 0.1, 0.45, 0xff4a10, 0.7 * k + 0.1, 1, 0.8);
      }
    }
  }

  clear(): void {
    this.glint = this.on = this.pose = 0; this.geoB.setDrawRange(0, 0); this.geoH.setDrawRange(0, 0); this.flares.count = 0;
    for (const tr of this.trails) { tr.n = 0; tr.acc = 0; }
  }
}
